<template>
  <SettingsPageAttackListSenderEditView
    v-if="setting && result"
    :setting="setting"
    :entrances="result.viewer.attackListSender.availableCorporationEntrances"
    @save="handleSave"
    @cancel="handleCancel"
    @delete="handleDelete"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, onBeforeRouteLeave } from 'vue-router'

import router from '@/router'
import { captureException as newrelicCaptureException } from '@/newrelic'
import { useAlert } from '@/libs/alertPortal'

import SettingsPageAttackListSenderEditView from './SettingsPageAttackListSenderEditView.vue'
import type { FormData } from './SettingsPageAttackListSenderEditView.vue'
import {
  useDeleteAttackListSenderSettingMutation,
  useFetchOnSettingsPageAttackListSenderUpdateViewQuery,
  useUpdateAttackListSenderSettingMutation,
} from './SettingsPageAttackListSenderUpdateView.generated'
import {
  type Range,
  createRange,
} from './SettingsPageAttackListSenderEditorOptions'
import { useLogger } from '@/logger/logger'

const route = useRoute('attackListSenderSettingUpdate')
const settingId = computed(() => route.params.attackListSenderSettingId)

const { result } = useFetchOnSettingsPageAttackListSenderUpdateViewQuery({
  attackListSenderSettingId: settingId.value.toString(),
})

const toRange = (
  min: Maybe<number> | null,
  max: Maybe<number> | null
): Range => {
  return {
    min: min ?? null,
    max: max ?? null,
  }
}

const setting = computed(() => {
  if (!result.value) {
    return undefined
  }

  const resultSetting = result.value.attackListSenderSetting

  return {
    title: resultSetting.title,
    isEnabled: resultSetting.isEnabled,
    entranceGuid: resultSetting.entrance.guid,
    body: resultSetting.body,
    selections: resultSetting.selections ?? [],
    age: createRange(
      toRange(resultSetting.rule.age?.min, resultSetting.rule.age?.max)
    ),
    gender: resultSetting.rule.gender,
    saving: createRange(
      toRange(
        resultSetting.rule.savingRange?.min,
        resultSetting.rule.savingRange?.max
      )
    ),
    annualIncome: createRange(
      toRange(
        resultSetting.rule.annualIncomeRange?.min,
        resultSetting.rule.annualIncomeRange?.max
      )
    ),
    investment: createRange(
      toRange(
        resultSetting.rule.investmentRange?.min,
        resultSetting.rule.investmentRange?.max
      )
    ),
    hasPartner: result.value?.attackListSenderSetting.rule.partner,
    familyAnnualIncome: createRange(
      toRange(
        resultSetting.rule.familyAnnualIncomeRange?.min,
        resultSetting.rule.familyAnnualIncomeRange?.max
      )
    ),
    hasChild: result.value?.attackListSenderSetting.rule.child,
    ageOfFirstChild: createRange(
      toRange(
        resultSetting.rule.ageOfFirstChildRange?.min,
        resultSetting.rule.ageOfFirstChildRange?.max
      )
    ),
    shouldSendIfSameCorporationApproached:
      resultSetting.rule.shouldSendIfSameCorporationApproached,
  }
})

const createSelections = (selections: string[]): string[] => {
  const filtered = selections.filter(selection => selection !== '')
  if (filtered.length === 0) {
    throw new Error('selections must not be empty')
  }
  return filtered
}

const { mutate: updateAttackListSenderSetting } =
  useUpdateAttackListSenderSettingMutation({})

const alert = useAlert()
const logger = useLogger()

const isCompleted = ref(false)

const handleSave = async (form: FormData) => {
  if (!form.entranceGuid) {
    alert.error('プランナーを選択してください')
    return
  }

  try {
    const result = await updateAttackListSenderSetting({
      input: {
        id: settingId.value.toString(),
        title: form.title,
        body: form.body,
        entranceGuid: form.entranceGuid,
        isEnabled: form.isEnabled,
        selections: createSelections(form.selections),
        rule: {
          savingRange: form.saving,
          annualIncomeRange: form.annualIncome,
          familyAnnualIncomeRange: form.familyAnnualIncome,
          partner: form.hasPartner,
          child: form.hasChild,
          ageOfFirstChildRange: form.ageOfFirstChild,
          gender: form.gender,
          age: form.age,
          investmentRange: form.investment,
          shouldSendIfSameCorporationApproached:
            form.shouldSendIfSameCorporationApproached,
        },
      },
    })

    const updatedSetting = result?.data?.updateAttackListSenderSetting
    if (!updatedSetting) {
      newrelicCaptureException(
        new Error('updatedAttackListSenderSetting is failed')
      )
      alert.error(
        'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
      )
      return
    }

    logger.ltlPeople.event.updateAttackListSenderSetting(logger.buildMeta(), {
      data: {
        id: Number(updatedSetting.id),
        plannerId: Number(updatedSetting.planner.id),
        entranceGuid: updatedSetting.entrance.guid,
        isEnabled: updatedSetting.isEnabled,
        rulesJson: JSON.stringify(updatedSetting.rule),
        title: updatedSetting.title,
        body: updatedSetting.body,
        selections: updatedSetting.selections,
      },
    })
    alert.success('アプローチ自動代行設定を保存しました')
    isCompleted.value = true
    // eslint-disable-next-line fp/no-mutating-methods
    router.push({ name: 'attackListSenderSetting' })
  } catch (e) {
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}

const deleteAttackListSenderSettingsMutation =
  useDeleteAttackListSenderSettingMutation()

const handleDelete = async () => {
  if (!window.confirm('この設定を削除しますか？')) {
    return
  }

  try {
    await deleteAttackListSenderSettingsMutation.mutate({
      ids: [settingId.value.toString()],
    })
    alert.success('アプローチ自動代行設定を削除しました')
    isCompleted.value = true
    // eslint-disable-next-line fp/no-mutating-methods
    router.push({ name: 'attackListSenderSetting' })
  } catch (e) {
    console.error(e)

    newrelicCaptureException(e)
    alert.error('削除に失敗しました、もう一度お試しください')
    return
  }
}

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const handleCancel = () => {
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({ name: 'attackListSenderSetting' })
}

onBeforeRouteLeave((to, from, next) => {
  if (from.name !== 'attackListSenderSettingUpdate') {
    next()
    return
  }
  if (to.name === 'attackListSenderSetting' && isCompleted.value) {
    isCompleted.value = false
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value = true
      next()
    },
    onAbort: () => next(false),
  })
})
</script>
